/**
 * Firebase-backed authentication and account service.
 *
 * Implements the AuthService interface (`../interfaces.js`) against Firebase
 * Authentication and a Firestore account record at `users/{uid}`.
 *
 * Security decisions worth calling out:
 *   - No password is ever stored, echoed or logged here; the SDK handles the
 *     credential and we keep only the derived, non-sensitive Account.
 *   - Sign-in failures are collapsed into a single generic message so the page
 *     cannot reveal whether an email address is registered (§16, account
 *     enumeration). Password-reset does the same by swallowing "user not found".
 *   - The client may create its own account record, but the Firestore rules pin
 *     the entitlement fields to a free default: the browser cannot grant itself a
 *     subscription. Authoritative subscription state will only ever be written
 *     server-side from verified Stripe webhooks (Phase 6).
 */

import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
} from "firebase/auth";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { POLICY_VERSION } from "../../config/policies.js";

/** The valid stored subscription states. Mirrors models.js SubscriptionStatus. */
const SUBSCRIPTION_STATUSES = [
  "none",
  "free",
  "trialling",
  "active",
  "past_due",
  "cancel_at_period_end",
  "expired",
  "unpaid",
];

/**
 * @param {unknown} value
 * @returns {import("../../types/models.js").SubscriptionStatus}
 */
function normaliseSubscriptionStatus(value) {
  return typeof value === "string" && SUBSCRIPTION_STATUSES.includes(value)
    ? /** @type {import("../../types/models.js").SubscriptionStatus} */ (value)
    : "none";
}

/**
 * Map a Firebase auth error to a plain-language, non-sensitive message.
 *
 * Sign-in codes that reveal whether an account exists (`user-not-found`,
 * `wrong-password`, `invalid-credential`) all collapse into one message so the UI
 * cannot be used to enumerate registered email addresses.
 *
 * @param {unknown} error
 * @returns {string}
 */
function friendlyAuthError(error) {
  const code =
    typeof error === "object" && error !== null && "code" in error
      ? String(/** @type {{ code: unknown }} */ (error).code)
      : "";

  switch (code) {
    case "auth/invalid-email":
      return "Enter a valid email address.";
    case "auth/email-already-in-use":
      return "An account already exists for that email address. Try signing in.";
    case "auth/weak-password":
      return "Choose a longer password. Use at least 12 characters.";
    case "auth/user-not-found":
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "Those sign-in details were not recognised. Check your email and password and try again.";
    case "auth/too-many-requests":
      return "Too many attempts. Please wait a little while before trying again.";
    case "auth/network-request-failed":
      return "Could not reach the server. Check your connection and try again.";
    default:
      return "Something went wrong. Please try again.";
  }
}

/**
 * @param {unknown} error
 * @returns {boolean}
 */
function isUserNotFound(error) {
  return (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    /** @type {{ code: unknown }} */ (error).code === "auth/user-not-found"
  );
}

/**
 * Build the display Account from a Firebase user, reading its subscription state
 * from the account record. A missing or unreadable record falls back to "none":
 * entitlements are never inferred optimistically.
 *
 * @param {import("firebase/firestore").Firestore} db
 * @param {import("firebase/auth").User} user
 * @returns {Promise<import("../../types/models.js").Account>}
 */
async function buildAccount(db, user) {
  let subscriptionStatus = /** @type {import("../../types/models.js").SubscriptionStatus} */ (
    "none"
  );
  /** @type {string | undefined} */
  let planId;
  /** @type {number | null} */
  let renewalAt = null;
  /** @type {number | null} */
  let gracePeriodEndsAt = null;
  let accountStatus = "active";
  /** @type {number | null} */
  let deletionRequestedAt = null;
  let termsVersion = 0;
  let privacyVersion = 0;
  try {
    const snapshot = await getDoc(doc(db, "users", user.uid));
    if (snapshot.exists()) {
      subscriptionStatus = normaliseSubscriptionStatus(snapshot.get("subscriptionStatus"));
      const storedPlan = snapshot.get("planId");
      planId = typeof storedPlan === "string" ? storedPlan : undefined;
      renewalAt = toMillisOrNull(snapshot.get("currentPeriodEnd"));
      gracePeriodEndsAt = toMillisOrNull(snapshot.get("gracePeriodEndsAt"));
      const storedStatus = snapshot.get("accountStatus");
      accountStatus = typeof storedStatus === "string" ? storedStatus : "active";
      deletionRequestedAt = toMillisOrNull(snapshot.get("deletionRequestedAt"));
      termsVersion =
        typeof snapshot.get("termsVersion") === "number" ? snapshot.get("termsVersion") : 0;
      privacyVersion =
        typeof snapshot.get("privacyVersion") === "number" ? snapshot.get("privacyVersion") : 0;
    }
  } catch {
    // A read failure must not block sign-in; the safe default already applies.
  }
  return {
    id: user.uid,
    email: user.email ?? "",
    emailVerified: user.emailVerified,
    subscriptionStatus,
    ...(planId ? { planId } : {}),
    renewalAt,
    gracePeriodEndsAt,
    accountStatus,
    deletionRequestedAt,
    termsVersion,
    privacyVersion,
  };
}

/**
 * Read a Firestore Timestamp field as epoch ms, tolerating its absence.
 *
 * @param {unknown} value
 * @returns {number | null}
 */
function toMillisOrNull(value) {
  return value && typeof (/** @type {{ toMillis?: unknown }} */ (value).toMillis) === "function"
    ? /** @type {{ toMillis: () => number }} */ (value).toMillis()
    : null;
}

/**
 * Create the account record if it does not yet exist. The write is deliberately
 * minimal and free-tier only; the Firestore rules reject any attempt to set a
 * richer subscription state.
 *
 * @param {import("firebase/firestore").Firestore} db
 * @param {import("firebase/auth").User} user
 * @returns {Promise<void>}
 */
async function ensureAccountRecord(db, user) {
  const reference = doc(db, "users", user.uid);
  const snapshot = await getDoc(reference);
  if (snapshot.exists()) {
    return;
  }
  await setDoc(reference, {
    accountStatus: "active",
    planId: "free",
    subscriptionStatus: "none",
    termsVersion: POLICY_VERSION.terms,
    privacyVersion: POLICY_VERSION.privacy,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * @param {import("./app.js").FirebaseContext} context
 * @returns {import("../interfaces.js").AuthService}
 */
export function createFirebaseAuthService(context) {
  const { auth, db } = context;

  /** @type {import("../../types/models.js").Account | null} */
  let account = null;
  let ready = false;

  /** @type {() => void} */
  let markReady = () => {};
  /** @type {Promise<void>} */
  const readyPromise = new Promise((resolve) => {
    markReady = () => resolve();
  });

  /** @type {Set<() => void>} */
  const listeners = new Set();

  function notify() {
    for (const listener of listeners) {
      listener();
    }
  }

  /**
   * Recompute the cached account from a Firebase user (or clear it) and notify.
   *
   * @param {import("firebase/auth").User | null} user
   * @returns {Promise<import("../../types/models.js").Account | null>}
   */
  async function applyUser(user) {
    account = user ? await buildAccount(db, user) : null;
    notify();
    return account;
  }

  /**
   * Build the account, cache it and notify — used by sign-in and register so the
   * returned (non-null) account and getCurrentAccount() are consistent before the
   * call resolves.
   *
   * @param {import("firebase/auth").User} user
   * @returns {Promise<import("../../types/models.js").Account>}
   */
  async function buildAccountAndCache(user) {
    const built = await buildAccount(db, user);
    account = built;
    notify();
    return built;
  }

  // The initial state resolves asynchronously; whenReady() only settles once the
  // first callback has fully applied, so getCurrentAccount() is populated by then.
  onAuthStateChanged(auth, async (user) => {
    await applyUser(user);
    if (!ready) {
      ready = true;
      markReady();
    }
  });

  return {
    getCurrentAccount() {
      return account;
    },

    isReady() {
      return ready;
    },

    whenReady() {
      return readyPromise;
    },

    async signIn(email, password) {
      try {
        const credential = await signInWithEmailAndPassword(auth, email, password);
        await ensureAccountRecord(db, credential.user);
        return await buildAccountAndCache(credential.user);
      } catch (error) {
        throw new Error(friendlyAuthError(error), { cause: error });
      }
    },

    async register(email, password) {
      try {
        const credential = await createUserWithEmailAndPassword(auth, email, password);
        await ensureAccountRecord(db, credential.user);
        try {
          await sendEmailVerification(credential.user);
        } catch {
          // A transient email failure must not fail account creation; the account
          // exists and the verification email can be resent from the app.
        }
        return await buildAccountAndCache(credential.user);
      } catch (error) {
        throw new Error(friendlyAuthError(error), { cause: error });
      }
    },

    async signOut() {
      await firebaseSignOut(auth);
      account = null;
      notify();
    },

    async sendVerificationEmail() {
      const user = auth.currentUser;
      if (!user) {
        throw new Error("Sign in to resend a verification email.");
      }
      try {
        await sendEmailVerification(user);
      } catch (error) {
        throw new Error(friendlyAuthError(error), { cause: error });
      }
    },

    async sendPasswordReset(email) {
      try {
        await sendPasswordResetEmail(auth, email);
      } catch (error) {
        // Never disclose whether the address is registered.
        if (isUserNotFound(error)) {
          return;
        }
        throw new Error(friendlyAuthError(error), { cause: error });
      }
    },

    async refresh() {
      const user = auth.currentUser;
      if (!user) {
        account = null;
        notify();
        return;
      }
      await user.reload();
      // Force a fresh ID token after the reload. reload() updates the in-memory
      // user (so emailVerified flips true), but it does NOT refresh the cached ID
      // token — its claims, including email_verified, stay stale for up to an hour.
      // Without this, a callable made straight after the user verifies their email
      // still sends the old token and is rejected server-side by
      // requireVerifiedCaller, which surfaces as "verify your email" on a screen
      // that just told them they were verified.
      await user.getIdToken(true);
      await applyUser(auth.currentUser);
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
